/**
 * Pure display resolution for FuturesCard (winner row + scorer kit/labels)
 * and leaderboard rows (API stores opaque team/scorer IDs).
 */

import { FUTURES_CARD_COPY } from './FuturesCard.constants';
import { PLAYER_INFO, type PlayerKitMeta } from './FuturesCard.players.constants';
import { TEAM_CODE } from '../MatchesView/MatchesView.constants';
import { scorerDisplayName } from '../../../src/worldcup/helpers/worldcup.helper';

/** Short UI labels for scorer rows where last-token logic is wrong (e.g. Estevão vs Oliveira). */
const FUTURES_TOP_SCORER_CAPTION_BY_ID: Record<string, string> = {
  'worldcup-topscorer-estevao-almeida-de-oliveira': 'Estevão',
  'worldcup-topscorer-estevao-willian-goncalves': 'Estevão',
};

export function futuresTopScorerCaption(
  optionId: string | null | undefined,
  label: string | null | undefined,
): string {
  const id = optionId?.trim();
  if (id && FUTURES_TOP_SCORER_CAPTION_BY_ID[id]) {
    return FUTURES_TOP_SCORER_CAPTION_BY_ID[id];
  }
  return scorerDisplayName(label ?? null);
}
import type {
  WorldCupFuturesOptions,
  WorldCupFuturesParticipantTeam,
  WorldCupFuturesTopScorerOption,
} from '../../../src/worldcup/helpers/worldcupPicksData.helper';

export function resolveWinnerParticipant(
  draftWinner: string | null,
  participantTeams: readonly WorldCupFuturesParticipantTeam[],
): WorldCupFuturesParticipantTeam | null {
  if (!draftWinner || participantTeams.length === 0) return null;
  return (
    participantTeams.find((t) => t.id === draftWinner)
    ?? participantTeams.find((t) => t.name === draftWinner)
    ?? null
  );
}

export function scorerTextLabelForId(
  draftScorer: string | null,
  futuresOptions: WorldCupFuturesOptions | null,
): string | null {
  if (!draftScorer) return null;
  if (!futuresOptions) return scorerDisplayName(draftScorer);
  if (futuresOptions.topScorerOtherId && draftScorer === futuresOptions.topScorerOtherId) {
    return FUTURES_CARD_COPY.PICK_OTHER_PLAYER_LABEL;
  }
  const opt = futuresOptions.topScorers?.find((o) => o.id === draftScorer);
  if (opt) return futuresTopScorerCaption(opt.id, opt.label);
  return scorerDisplayName(draftScorer);
}

/** Leaderboard scorer line only; skips fallback text when there is no resolved kit (no raw opaque ids). */
function resolveLeaderboardScorerCaption(
  scorerId: string | null,
  futuresOptions: WorldCupFuturesOptions | null,
  scorerKitMeta: PlayerKitMeta | null,
): string | null {
  if (!scorerId) return null;
  const otherId = futuresOptions?.topScorerOtherId;
  if (otherId && scorerId === otherId) return FUTURES_CARD_COPY.PICK_OTHER_PLAYER_LABEL;

  const option = futuresOptions?.topScorers?.find((o) => o.id === scorerId);
  if (option) return futuresTopScorerCaption(option.id, option.label) || null;

  if (!scorerKitMeta) return null;
  return (scorerTextLabelForId(scorerId, futuresOptions) ?? '') || null;
}

export function scorerKitMetaForCard(
  draftScorer: string | null,
  futuresOptions: WorldCupFuturesOptions | null,
): PlayerKitMeta | null {
  if (!draftScorer) return null;
  if (!futuresOptions?.topScorers?.length) {
    return PLAYER_INFO[draftScorer] ?? null;
  }
  if (futuresOptions.topScorerOtherId && draftScorer === futuresOptions.topScorerOtherId) {
    return null;
  }
  const opt = futuresOptions.topScorers.find((o) => o.id === draftScorer);
  if (opt && opt.number !== undefined && opt.kitColor !== undefined) {
    return {
      number: opt.number,
      kitColor: opt.kitColor,
      trimColor: opt.trimColor,
      countryCode: opt.countryCode,
    };
  }
  return PLAYER_INFO[draftScorer] ?? null;
}

export function kitMetaFromScorerOption(o: WorldCupFuturesTopScorerOption): PlayerKitMeta | null {
  if (o.number === undefined || o.kitColor === undefined) return null;
  return {
    number: o.number,
    kitColor: o.kitColor,
    trimColor: o.trimColor,
    countryCode: o.countryCode,
  };
}

export function scorerKitPlayerNameForCard(
  draftScorer: string | null,
  futuresOptions: WorldCupFuturesOptions | null,
): string | null {
  if (!draftScorer) return null;
  if (!futuresOptions?.topScorers?.length) return draftScorer;
  const opt = futuresOptions.topScorers.find((o) => o.id === draftScorer);
  return opt?.label ?? draftScorer;
}

/** Resolved winner flag + captions and scorer kit for leaderboard rows. */
export interface LeaderboardFuturesDisplayResolved {
  winnerCountryCode: string | null;
  winnerCaption: string | null;
  scorerKitMeta: PlayerKitMeta | null;
  scorerPlayerNameForKit: string | null;
  scorerCaption: string | null;
}

/**
 * Maps API futures IDs to display data using the same options as GET …/matches.
 * Falls back to TEAM_CODE + team name when options are missing (e.g. mocks / offline).
 */
export function resolveLeaderboardFuturesDisplay(
  winnerTeamId: string | null | undefined,
  topScorerPlayerId: string | null | undefined,
  futuresOptions: WorldCupFuturesOptions | null,
): LeaderboardFuturesDisplayResolved {
  const wId = winnerTeamId ?? null;
  const sId = topScorerPlayerId ?? null;

  const teams = futuresOptions?.participantTeams ?? [];
  const participant = resolveWinnerParticipant(wId, teams);
  let winnerCountryCode: string | null = null;
  let winnerCaption: string | null = null;
  if (participant) {
    winnerCountryCode = participant.countryCode?.trim() || null;
    winnerCaption = participant.name;
  } else if (wId && TEAM_CODE[wId]) {
    winnerCountryCode = TEAM_CODE[wId];
    winnerCaption = wId;
  }

  const scorerKitMeta = scorerKitMetaForCard(sId, futuresOptions);
  const scorerPlayerNameForKit = scorerKitPlayerNameForCard(sId, futuresOptions);
  const scorerCaption = resolveLeaderboardScorerCaption(sId, futuresOptions, scorerKitMeta);

  return {
    winnerCountryCode,
    winnerCaption,
    scorerKitMeta,
    scorerPlayerNameForKit,
    scorerCaption,
  };
}
